import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as ordiniApi from '@/api/ordini'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Truck, Search, Eye, Package } from 'lucide-react'
import DdtButton from '@/components/ordini/DdtButton'

const STATO_COLORS = {
  preparazione: 'secondary',
  pronto: 'default',
  ritirato: 'default',
  in_transito: 'warning',
  in_consegna: 'warning',
  consegnato: 'success',
  problema: 'destructive',
  reso: 'destructive',
}

export default function SpedizioniList() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [stato, setStato] = useState('')
  const [ordering, setOrdering] = useSortingState('spedizioni', '-data_spedizione')

  const { page, setPage } = useListPagination({ search, stato, ordering })

  const { data: spedizioni, isLoading, isFetching, error } = useQuery({
    queryKey: ['spedizioni', { page, search, stato, ordering }],
    queryFn: () => ordiniApi.getSpedizioni({ page, search, stato, ordering }).then(res => res.data),
  })

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const renderDestinatari = (nomi = []) => {
    if (nomi.length === 0) return <span className="text-muted-foreground">-</span>
    if (nomi.length === 1) return <span>{nomi[0]}</span>
    return (
      <div title={nomi.join(', ')}>
        <div className="font-medium">{nomi.length} destinatari</div>
        <div className="max-w-48 truncate text-xs text-muted-foreground">
          {nomi.join(', ')}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Spedizioni</h1>
          <p className="text-muted-foreground">Gestione spedizioni ordini</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Lista Spedizioni
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per spedizione, ordine, cliente, destinatario o indirizzo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={stato}
              onChange={(e) => setStato(e.target.value)}
              className="border rounded-md px-3"
            >
              <option value="">Tutti gli stati</option>
              <option value="preparazione">In Preparazione</option>
              <option value="pronto">Pronto per Ritiro</option>
              <option value="ritirato">Ritirato</option>
              <option value="in_transito">In Transito</option>
              <option value="in_consegna">In Consegna</option>
              <option value="consegnato">Consegnato</option>
              <option value="problema">Problema</option>
              <option value="reso">Reso</option>
            </select>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Caricamento...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="codice" currentOrdering={ordering} onSort={setOrdering}>Spedizione</SortableTableHead>
                  <SortableTableHead field="ordine__numero" currentOrdering={ordering} onSort={setOrdering}>Ordine</SortableTableHead>
                  <SortableTableHead field="cliente_nome" currentOrdering={ordering} onSort={setOrdering}>Cliente</SortableTableHead>
                  <SortableTableHead field="indirizzo_completo" currentOrdering={ordering} onSort={setOrdering}>Indirizzo</SortableTableHead>
                  <SortableTableHead field="destinatari_nomi" currentOrdering={ordering} onSort={setOrdering}>Destinatario</SortableTableHead>
                  <SortableTableHead field="stato" currentOrdering={ordering} onSort={setOrdering}>Stato</SortableTableHead>
                  <SortableTableHead field="numero_colli" currentOrdering={ordering} onSort={setOrdering} className="text-center">Colli</SortableTableHead>
                  <SortableTableHead field="data_spedizione" currentOrdering={ordering} onSort={setOrdering}>
                    Data Spedizione
                  </SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {spedizioni?.results?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Nessuna spedizione trovata
                    </TableCell>
                  </TableRow>
                ) : (
                  spedizioni?.results?.map((spedizione) => (
                    <TableRow key={spedizione.id}>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/ordini/spedizioni/${spedizione.id}`)}
                          className="font-mono font-medium text-primary hover:underline"
                        >
                          {spedizione.codice || `Spedizione #${spedizione.id}`}
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/ordini/${spedizione.ordine}`)}
                          className="font-mono font-medium text-primary hover:underline"
                        >
                          {spedizione.ordine_numero}
                        </button>
                      </TableCell>
                      <TableCell className="font-medium">
                        {spedizione.cliente_nome || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-56 text-sm" title={spedizione.indirizzo_completo}>
                          <div className="truncate">
                            {[spedizione.indirizzo_via, spedizione.indirizzo_civico].filter(Boolean).join(' ') || '-'}
                          </div>
                          <div className="truncate text-muted-foreground">
                            {[spedizione.indirizzo_cap, spedizione.indirizzo_citta].filter(Boolean).join(' ')}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {renderDestinatari(spedizione.destinatari_nomi)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATO_COLORS[spedizione.stato] || 'secondary'}>
                          {spedizione.stato_display}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{spedizione.numero_colli || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(spedizione.data_spedizione)}</TableCell>
                      <TableCell className="text-right">
                        <DdtButton spedizione={spedizione} variant="outline" size="sm" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/ordini/spedizioni/${spedizione.id}`)}
                          title="Visualizza dettaglio spedizione"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <ListPagination data={spedizioni} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
